import { useEffect, useState } from "react";
import { Scale, Loader2, Save, History, ShieldCheck, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { toast } from "sonner";

const num = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

function FilaFraccion({ f, uma, onGuardado, onBitacora }) {
  const [edit, setEdit] = useState({
    identificacion_uma: num(f.identificacion_uma),
    aviso_uma: num(f.aviso_uma),
    efectivo_uma: num(f.efectivo_uma),
    ventana_acumulacion_dias: num(f.ventana_acumulacion_dias),
    activa: !!f.activa,
    motivo: "",
  });
  const [guardando, setGuardando] = useState(false);
  const sucio =
    num(f.identificacion_uma) !== edit.identificacion_uma ||
    num(f.aviso_uma) !== edit.aviso_uma ||
    num(f.efectivo_uma) !== edit.efectivo_uma ||
    num(f.ventana_acumulacion_dias) !== edit.ventana_acumulacion_dias ||
    !!f.activa !== edit.activa;

  const mxn = (u) =>
    u === "" || u === null ? "—" : `$${(Number(u) * uma).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/admin/umbrales-fracciones/${f.fraccion}`, {
        identificacion_uma: edit.identificacion_uma === "" ? null : Number(edit.identificacion_uma),
        aviso_uma: edit.aviso_uma === "" ? null : Number(edit.aviso_uma),
        efectivo_uma: edit.efectivo_uma === "" ? null : Number(edit.efectivo_uma),
        ventana_acumulacion_dias: Number(edit.ventana_acumulacion_dias),
        activa: edit.activa,
        motivo: edit.motivo,
      });
      toast.success(`Fr. ${f.fraccion} actualizada — surte efecto de inmediato`);
      onGuardado();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <tr className="border-b border-slate-100 align-top hover:bg-slate-50/70" data-testid={`fila-umbral-${f.fraccion}`}>
      <td className="px-3 py-3">
        <div className="font-semibold text-slate-800">Fr. {f.fraccion.replace("_bis", " Bis")}</div>
        <div className="max-w-[22rem] text-xs text-slate-500">{f.actividad}</div>
        {f.verificado_norma ? (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700">
            <ShieldCheck className="h-3 w-3" /> Verificada contra la tabla del SAT
          </span>
        ) : (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
            <AlertTriangle className="h-3 w-3" /> Requiere confirmación
          </span>
        )}
        {f.nota_duda ? (
          <div className="mt-1 max-w-[22rem] rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
            {f.nota_duda}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-3">
        {f.identificacion_siempre ? (
          <span className="text-sm font-medium text-slate-700">Siempre</span>
        ) : (
          <>
            <Input
              data-testid={`input-ident-${f.fraccion}`}
              className="h-8 w-24"
              value={edit.identificacion_uma}
              onChange={(e) => setEdit({ ...edit, identificacion_uma: e.target.value })}
            />
            <div className="mt-1 text-[11px] text-slate-500">{mxn(edit.identificacion_uma)}</div>
          </>
        )}
      </td>
      <td className="px-3 py-3">
        {f.aviso_siempre ? (
          <span className="text-sm font-medium text-slate-700">Siempre</span>
        ) : f.aviso_condicional ? (
          <span className="max-w-[14rem] text-xs text-slate-600">Condicional al acto</span>
        ) : (
          <>
            <Input
              data-testid={`input-aviso-${f.fraccion}`}
              className="h-8 w-24"
              value={edit.aviso_uma}
              onChange={(e) => setEdit({ ...edit, aviso_uma: e.target.value })}
            />
            <div className="mt-1 text-[11px] text-slate-500">{mxn(edit.aviso_uma)}</div>
          </>
        )}
      </td>
      <td className="px-3 py-3">
        <Input
          data-testid={`input-efectivo-${f.fraccion}`}
          className="h-8 w-24"
          value={edit.efectivo_uma}
          onChange={(e) => setEdit({ ...edit, efectivo_uma: e.target.value })}
        />
        <div className="mt-1 text-[11px] text-slate-500">
          {f.efectivo_articulo ? `Art. ${f.efectivo_articulo} · ` : ""}
          {mxn(edit.efectivo_uma)}
        </div>
      </td>
      <td className="px-3 py-3 text-xs text-slate-600">
        {f.base_iva === "sin_iva" ? "Sin IVA" : "Con IVA"}
        <div className="text-[11px] text-slate-400">Efectivo: con IVA</div>
      </td>
      <td className="px-3 py-3">
        <Input
          data-testid={`input-ventana-${f.fraccion}`}
          className="h-8 w-20"
          value={edit.ventana_acumulacion_dias}
          onChange={(e) => setEdit({ ...edit, ventana_acumulacion_dias: e.target.value })}
        />
        <div className="mt-1 text-[11px] text-slate-500">días</div>
      </td>
      <td className="px-3 py-3">
        <Switch
          data-testid={`switch-activa-${f.fraccion}`}
          checked={edit.activa}
          onCheckedChange={(v) => setEdit({ ...edit, activa: v })}
        />
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-col gap-2">
          {sucio ? (
            <Input
              data-testid={`input-motivo-${f.fraccion}`}
              className="h-8 w-40"
              placeholder="Motivo del cambio"
              value={edit.motivo}
              onChange={(e) => setEdit({ ...edit, motivo: e.target.value })}
            />
          ) : null}
          <div className="flex gap-2">
            <Button
              data-testid={`btn-guardar-${f.fraccion}`}
              size="sm"
              disabled={!sucio || guardando}
              onClick={guardar}
            >
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            </Button>
            <Button
              data-testid={`btn-bitacora-${f.fraccion}`}
              size="sm"
              variant="outline"
              onClick={() => onBitacora(f.fraccion)}
            >
              <History className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-[11px] text-slate-400">
            {f.actualizado_por ? `${f.actualizado_por} · ${fdatetime(f.actualizado_en)}` : ""}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AdminUmbrales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uma, setUma] = useState(117.31);
  const [bitacora, setBitacora] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/umbrales-fracciones");
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el catálogo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const verBitacora = async (fraccion) => {
    try {
      const { data } = await api.get(`/admin/umbrales-fracciones/${fraccion}/bitacora`);
      setBitacora({ fraccion, registros: data });
    } catch (e) {
      toast.error("No se pudo cargar la bitácora");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-umbrales">
      <PageHeader
        icon={Scale}
        title="Umbrales LFPIORPI Art. 17"
        subtitle="Catálogo paramétrico de las 17 fracciones. Los cambios surten efecto de inmediato y quedan en bitácora."
      />
      <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
        Base de cálculo: <b>UMA diaria ${uma}</b> (vigencia feb-2026 → ene-2027). Art. 17 se evalúa
        <b> sin IVA</b>; la restricción de efectivo del Art. 32, <b>con IVA</b> y accesorios.
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-slate-300" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Fracción / actividad</th>
                <th className="px-3 py-2">Identificación (UMA)</th>
                <th className="px-3 py-2">Aviso (UMA)</th>
                <th className="px-3 py-2">Efectivo Art. 32 (UMA)</th>
                <th className="px-3 py-2">Base</th>
                <th className="px-3 py-2">Acumulación</th>
                <th className="px-3 py-2">Activa</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <FilaFraccion
                  key={f.fraccion}
                  f={f}
                  uma={uma}
                  onGuardado={load}
                  onBitacora={verBitacora}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bitacora ? (
        <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="bitacora-umbrales">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-slate-800">
              Bitácora — Fr. {bitacora.fraccion}
            </h3>
            <Button size="sm" variant="outline" onClick={() => setBitacora(null)} data-testid="btn-cerrar-bitacora">
              Cerrar
            </Button>
          </div>
          {bitacora.registros.length === 0 ? (
            <p className="text-sm text-slate-500">Sin cambios registrados.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-1">Fecha</th>
                  <th className="py-1">Usuario</th>
                  <th className="py-1">Campo</th>
                  <th className="py-1">Anterior</th>
                  <th className="py-1">Nuevo</th>
                  <th className="py-1">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {bitacora.registros.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1">{fdatetime(r.fecha)}</td>
                    <td className="py-1">{r.usuario}</td>
                    <td className="py-1 font-mono text-xs">{r.campo}</td>
                    <td className="py-1">{String(r.valor_anterior)}</td>
                    <td className="py-1 font-semibold">{String(r.valor_nuevo)}</td>
                    <td className="py-1 text-xs text-slate-500">{r.motivo || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  );
}
